import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  BuildingOfficeIcon,
  GlobeAltIcon,
  IdentificationIcon,
  PhoneIcon,
  UserCircleIcon,
} from "@heroicons/react/20/solid";
import { PlusIcon } from "@heroicons/react/24/outline";
import { useNavigate } from "react-router-dom";

import FormInput from "../../components/controls/FormInput";
import Autocomplete from "../../components/Places/PlacesAutocomplete";
import ImageUploader from "../../components/controls/ImageUploader";
import { useAuth } from "../../contexts/AuthContext";
import {
  fetchCompaniesByUserId,
  addOrUpdateCompanies,
} from "../../db/Companies";

// ✅ NOS COMPOSANTS
import Button from "../../components/controls/Button";
import PageHeader from "../../components/controls/PageHeader";
import AnanasSolutionsLogo from "../../images/AnanasSolutionsLogo.webp";
import Tabs from "../../components/controls/Tabs";

export default function RegisterCompany() {
  const { t } = useTranslation();
  const { currentUser } = useAuth();
  const userId = currentUser?.id || null;
  const navigate = useNavigate();

  const [companies, setCompanies] = useState([createEmptyCompany(userId)]);
  const [activeCompanyIndex, setActiveCompanyIndex] = useState(0);

  if (!userId) {
    navigate("/login");
  }

  function createEmptyCompany(userId) {
    return {
      name: "",
      neqNumber: "",
      address: "",
      phoneNumber: "",
      contactPerson: "",
      website: "",
      contactEmail: "",
      imageUrl: "",
      subscriptionTier: 1,
      userId,
      location: null,
      tempImageUrl: "",
    };
  }

  useEffect(() => {
    const loadCompanies = async () => {
      if (!userId) return;
      const result = await fetchCompaniesByUserId(userId);
      if (result.length > 0) setCompanies(result);
    };
    loadCompanies();
  }, [userId]);

  const handleCompanyChange = (field, value) => {
    const updated = [...companies];
    if (field === "location") {
      updated[activeCompanyIndex].location = {
        ...updated[activeCompanyIndex].location,
        ...value,
      };
    } else {
      updated[activeCompanyIndex][field] = value;
    }
    setCompanies(updated);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const payload = companies.map(
      ({ tempImageUrl, hasChangedImageUrl, ...rest }) => ({
        ...rest,
        imageUrl: hasChangedImageUrl ? tempImageUrl ?? null : rest.imageUrl,
      })
    );
    try {
      const responseIds = await addOrUpdateCompanies(payload);
      if (
        Array.isArray(responseIds) &&
        responseIds.length === companies.length
      ) {
        setCompanies((prev) =>
          prev.map((c, i) => ({ ...c, id: responseIds[i] }))
        );
      }
      alert(t("saved"));
    } catch (error) {
      console.error("Erreur lors de la soumission", error);
      alert(t("errors.submission"));
    }
  };

  const addCompany = () => {
    setCompanies((prev) => [...prev, createEmptyCompany(userId)]);
    setActiveCompanyIndex(companies.length);
  };

  return (
    <div className="overflow-x-hidden">
      {/* ✅ PageHeader (plein écran) */}
      <PageHeader
        title={t("navbar.createCompany")}
        subtitle={t("navbar.createCompanyDescription")}
        logoSrc={AnanasSolutionsLogo}
        align="center"
        fullBleed
        rounded={false}
        height="sm"
        gradient={{ from: "#E9FBFA", via: "#FFF9E6", to: "#FFE9EC" }}
        patternSize="32px 32px"
        patternOpacity={15}
      />

      {/* Contenu */}
      <div className="w-max-[90%]  mx-auto  shadow-lg p-6 rounded-lg mt-6 text-primary">
        {/* ✅ Bouton “Ajouter une entreprise” avec notre Button */}
        <div className="mb-4 flex justify-end">
          <Button
            type="button"
            onClick={addCompany}
            color="primary"
            size="sm"
            leftIcon={<PlusIcon className="w-5 h-5" />}
          >
            {t("adminUserTable.addCompany")}
          </Button>
        </div>
        {/* Tabs (on garde ta logique existante) */}
        {companies.length > 1 && (
          <Tabs
            tabs={companies.map((c, i) => ({
              key: i,
              label: c.name?.trim() || t("adminUserTable.newCompany"),
            }))}
            activeIndex={activeCompanyIndex}
            onChange={setActiveCompanyIndex}
          />
        )}

        <form
          onSubmit={handleSubmit}
          className="w-full border border-ananas p-4 rounded-b-lg rounded-r-lg bg-white"
        >
          <div className="flex flex-col lg:flex-row gap-4 items-start">
            <div className="w-full lg:w-2/3 space-y-4">
              <div className="flex flex-col sm:flex-row gap-4 ">
                <div className="w-full sm:w-1/2">
                  <FormInput
                    label={t("becomeProvider.companyNameLabel")}
                    value={companies[activeCompanyIndex].name}
                    placeholder={t("becomeProvider.companyNamePlaceholder")}
                    onChange={(name, value) =>
                      handleCompanyChange("name", value)
                    }
                    required
                    icon={
                      <BuildingOfficeIcon className="w-5 h-5 text-gray-500 " />
                    }
                  />
                </div>

                <div className="w-full sm:w-1/2">
                  <Autocomplete
                    onSelect={(e) => {
                      handleCompanyChange("location", e);
                      handleCompanyChange("address", e.formattedAddress);
                    }}
                    initialAddress={companies[activeCompanyIndex].address || ""}
                  />
                </div>
              </div>

              <div className="flex flex-col sm:flex-row gap-4">
                <div className="w-full sm:w-1/2">
                  <FormInput
                    label={t("becomeProvider.contactEmailLabel")}
                    value={companies[activeCompanyIndex].contactEmail || ""}
                    placeholder={t("becomeProvider.contactEmailPlaceholder")}
                    onChange={(name, value) =>
                      handleCompanyChange("contactEmail", value)
                    }
                    required
                  />
                </div>

                <div className="w-full sm:w-1/2">
                  <FormInput
                    label={t("becomeProvider.phoneNumberLabel")}
                    value={companies[activeCompanyIndex].phoneNumber}
                    placeholder={t("becomeProvider.phoneNumberPlaceholder")}
                    onChange={(name, value) =>
                      handleCompanyChange("phoneNumber", value)
                    }
                    icon={<PhoneIcon className="w-5 h-5 text-gray-500" />}
                    required
                  />
                </div>
              </div>

              <div className="flex flex-col sm:flex-row gap-4">
                <div className="w-full sm:w-1/2">
                  <FormInput
                    label={t("becomeProvider.contactPersonLabel")}
                    value={companies[activeCompanyIndex].contactPerson}
                    placeholder={t("becomeProvider.contactPersonPlaceholder")}
                    onChange={(name, value) =>
                      handleCompanyChange("contactPerson", value)
                    }
                    icon={<UserCircleIcon className="w-5 h-5 text-gray-500" />}
                    required
                  />
                </div>

                <div className="w-full sm:w-1/2">
                  <FormInput
                    label={t("becomeProvider.neqNumberLabel")}
                    value={companies[activeCompanyIndex].neqNumber}
                    placeholder={t("becomeProvider.neqNumberPlaceholder")}
                    onChange={(name, value) =>
                      handleCompanyChange("neqNumber", value)
                    }
                    icon={
                      <IdentificationIcon className="w-5 h-5 text-gray-500" />
                    }
                  />
                </div>
              </div>

              <div className="flex flex-col sm:flex-row gap-4">
                <div className="w-full sm:w-1/2">
                  <FormInput
                    label={t("becomeProvider.websiteLabel")}
                    value={companies[activeCompanyIndex].website}
                    placeholder={t("becomeProvider.websitePlaceholder")}
                    onChange={(name, value) =>
                      handleCompanyChange("website", value)
                    }
                    icon={<GlobeAltIcon className="w-5 h-5 text-gray-500" />}
                  />
                </div>

                <div className="w-full sm:w-1/2" />
              </div>
            </div>

            <div className="w-full lg:w-1/3 lg:sticky lg:top-4 self-start">
              <div className="max-h-[520px] overflow-auto">
                <ImageUploader
                  ratio="square"
                  initialImageUrl={
                    companies[activeCompanyIndex]?.tempImageUrl ||
                    companies[activeCompanyIndex]?.imageUrl ||
                    ""
                  }
                  onTempImageChange={(url) => {
                    const updated = [...companies];
                    updated[activeCompanyIndex].tempImageUrl = url?.trim()
                      ? url
                      : null;
                    updated[activeCompanyIndex].hasChangedImageUrl = true;
                    setCompanies(updated);
                  }}
                />
              </div>
            </div>
          </div>

          <div className="flex justify-center border-t pt-4 mt-4">
            <Button type="submit" color="primary" size="lg">
              {t("modal.save")}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}
